import axios, { rawAxios } from '@/lib/axios'
import { buildAdminRequestConfig, type OrganizationId } from '@/lib/organization-scoping'
import type { QueryFunctionContext } from '@tanstack/react-query'
import type {
  AddOnSet,
  AddOnSetIdInput,
  AddOnSetIndexFilters,
  AddOnSetInput,
  UpdateAddOnSetInput,
  AddOnSetListItem,
  DeletedAddOnSet,
} from './addOnSets.types'

export interface AddOnSetOrganizationScope {
  organizationId: OrganizationId
}

/** Calls that read or change one add-on set; the Laravel route requires its id. */
export interface AddOnSetScope extends AddOnSetOrganizationScope {
  addOnSetId: AddOnSetIdInput
}

export interface AddOnSetIndexScope extends AddOnSetOrganizationScope {
  filters?: AddOnSetIndexFilters | undefined
}

/** The show query key's entries; the shared key builder can't mark an entry required. */
export interface AddOnSetShowKeyEntries extends AddOnSetOrganizationScope {
  addOnSetId?: AddOnSetIdInput
}

/** Query functions read only the key (and the abort signal when the caller passes one). */
type AddOnSetQueryContext<TKey> = Pick<QueryFunctionContext<[TKey]>, 'queryKey'> &
  Partial<Pick<QueryFunctionContext<[TKey]>, 'signal'>>

export async function getAddOnSets({
  queryKey: [{ organizationId, filters = {} }],
  signal,
}: AddOnSetQueryContext<AddOnSetIndexScope>) {
  const response = await rawAxios.get<AddOnSetListItem[]>(
    '/products/add-on-sets',
    buildAdminRequestConfig({
      organizationId,
      params: { ...filters },
      ...(signal ? { signal } : {}),
    })
  )

  return response?.data
}

export async function getAddOnSet({
  queryKey: [{ organizationId, addOnSetId }],
}: AddOnSetQueryContext<AddOnSetShowKeyEntries>) {
  const response = await rawAxios.get<AddOnSet>(
    `/products/add-on-sets/${addOnSetId}`,
    buildAdminRequestConfig({ organizationId })
  )

  return response?.data
}

export async function createAddOnSet({
  organizationId,
  ...values
}: AddOnSetInput & AddOnSetOrganizationScope) {
  return axios.post<AddOnSet>(
    '/products/add-on-sets',
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function updateAddOnSet({
  organizationId,
  addOnSetId,
  ...values
}: UpdateAddOnSetInput & AddOnSetScope) {
  return axios.put<AddOnSet>(
    `/products/add-on-sets/${addOnSetId}`,
    values,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function deleteAddOnSet({ organizationId, addOnSetId }: AddOnSetScope) {
  return axios.delete<DeletedAddOnSet>(
    `/products/add-on-sets/${addOnSetId}`,
    buildAdminRequestConfig({ organizationId })
  )
}
